import { useEffect } from "react";

const SITE = "Fieldistry";
// Single canonical host for the whole site (chosen: www). Everything points here
// regardless of whether the visitor landed on www or the apex domain.
const CANONICAL_ORIGIN = "https://www.fieldistry.com";
const DEFAULT_DESC = "Business operations software for trade, service, construction and field-work companies. Customers, jobs, crews, materials, scheduling and field work, one system.";
const DEFAULT_IMAGE = `${CANONICAL_ORIGIN}/og-image.png`;

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Seo({ title, description, path = "", keywords, jsonLd, image, noindex }) {
  useEffect(() => {
    const full = title ? `${title} | ${SITE}` : `${SITE}, Run Your Whole Operation in One Place`;
    const desc = description || DEFAULT_DESC;
    const url = CANONICAL_ORIGIN + path;
    const img = image || DEFAULT_IMAGE;
    document.title = full;
    setMeta("name", "description", desc);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    if (keywords) setMeta("name", "keywords", keywords);
    setMeta("property", "og:title", full);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", img);
    setMeta("property", "og:site_name", SITE);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", full);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", img);
    setCanonical(url);

    // Structured data (JSON-LD) for rich results, e.g. FAQ schema on industry pages
    let ldEl = null;
    if (jsonLd) {
      ldEl = document.createElement("script");
      ldEl.type = "application/ld+json";
      ldEl.setAttribute("data-seo-jsonld", "true");
      ldEl.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(ldEl);
    }

    // anonymous funnel page-view (no private data)
    try {
      let vid = localStorage.getItem("fieldistry_vid");
      if (!vid) { vid = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("fieldistry_vid", vid); }
      const utm = {};
      new URLSearchParams(window.location.search).forEach((v, k) => {
        if (k.startsWith("utm_")) utm[k.replace("utm_", "")] = v;
      });
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/track`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event: "page_view", page: path || window.location.pathname,
          utm, referrer: document.referrer, visitor_id: vid }),
      }).catch(() => {});
    } catch { /* tracking must never break the page */ }

    return () => { if (ldEl && ldEl.parentNode) ldEl.parentNode.removeChild(ldEl); };
  }, [title, description, path, keywords, jsonLd, image, noindex]);
  return null;
}
